import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useState, type MouseEvent } from 'react';
import HeartbeatIcon from './HeartbeatIcon';
import { DEFAULT_HEARTBEAT_MINUTES, isHeartbeatConfigured, isHeartbeatEnabled } from '../lib/taskHeartbeat';
import * as api from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { useToast } from './Toast';

/**
 * One projection of heartbeat configuration plus task-specific runtime state.
 * The control deliberately changes only `heartbeat_enabled`: interval and
 * prompt are retained by the server and task lifecycle is never touched.
 */
export default function TaskHeartbeatControl({ task, size = 16, className, onUpdated }: {
  task: api.Task;
  size?: number;
  className?: string;
  onUpdated?: (task: api.Task) => void;
}) {
  const { i18n } = useLingui();
  const { showToast } = useToast();
  const [updating, setUpdating] = useState(false);
  const configured = isHeartbeatConfigured(task);
  const enabled = isHeartbeatEnabled(task);

  // Every newly-created task receives a 5-minute schedule. A zero interval can
  // only be a legacy payload, and is still rendered as the same disabled control
  // rather than becoming a third, unexplained visual state.
  const nextEnabled = !enabled
  const label = nextEnabled ? i18n._(msg`Enable heartbeat`) : i18n._(msg`Disable heartbeat`)
  const runActive = enabled

  const toggle = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (updating) return;
    setUpdating(true);
    try {
      const updated = await api.updateTask(task.id, configured
        ? { heartbeat_enabled: nextEnabled }
        : { heartbeat_enabled: nextEnabled, heartbeat_interval_ms: DEFAULT_HEARTBEAT_MINUTES * 60 * 1000 });
      useTaskStore.getState().upsertTask(updated);
      onUpdated?.(updated);
    } catch (error) {
      showToast(errorMessage(error, msg`Failed to update heartbeat`));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <button
      type="button"
      className={`task-heartbeat-control${className ? ` ${className}` : ''}`}
      title={label}
      aria-label={label}
      aria-pressed={enabled}
      aria-busy={updating || undefined}
      disabled={updating}
      onClick={toggle}
    >
      <HeartbeatIcon size={size} active={runActive} disabled={!enabled} />
    </button>
  );
}
